import * as yup from 'yup';

import CrudPage from '@modules/cruds/CrudPage';
import EGRForm from './EGRForm';
import { defaultConfigComponent } from './EventGenerationRules';
import { useAppDispatch } from '@redux/store';
import { CEGR } from '@ts/ui-models';
import { globalActions } from '@redux/slices/states/global/globalSlice';

const defaultValues = {
  timeoutConfig: {
    noEventRecordedTypeId: null,
  },
  actionConfig: {
    noChangeStatusTriggerField: 'lastChange',
  },
  state: 'enabled',
};

const timeoutConfigSchema = yup.object({
  triggerDelay: yup.number().integer().nullable(),
  deadlineTriggerDelay: yup
    .number()
    .typeError('deadlineTriggerDelay debe ser numérico')
    .integer()
    .moreThan(0, 'deadlineTriggerDelay debe ser > 0')
    .required('deadlineTriggerDelay es obligatorio'),

  thresholdTriggerDelay: yup
    .number()
    .integer()
    .moreThan(0, 'thresholdTriggerDelay debe ser > 0')
    .nullable()
    .test('lt-deadline', 'The threshold trigger delay must be less than the deadline trigger delay', function (value) {
      // this.parent es el objeto timeoutConfig
      const deadline = this.parent?.deadlineTriggerDelay;
      if (value == null || value === 0) return true; // sin umbral => OK
      if (!(typeof deadline === 'number' && deadline > 0)) return true; // deja que otra regla valide deadline
      return value < deadline;
    }),
});

const validationSchema = yup
  .object()
  .shape({
    name: yup.string().required().label('Name'),
    eventTypeId: yup.string().required().label('Event Type'),
    actionLogicalId: yup.string().required().label('Action'),
    selectedLocationTypes: yup.array().of(yup.string()),
    selectedLocations: yup.array().of(yup.string()),
    selectedReadingTypes: yup.array().of(yup.string()),
    selectedItems: yup.array().of(yup.string()),
    selectedItemTypes: yup.array().of(yup.string()),
    selectedElementLabels: yup.array().of(yup.string()),
    timeoutConfig: yup.mixed().when('actionLogicalId', {
      is: (val: string) => val === 'noChangeStatus',
      then: timeoutConfigSchema.required('Timeout configuration is required when action is No Change Status'),
      otherwise: yup.mixed().notRequired(),
    }),
  })
  .test('afftected-item-required', 'affectedItemsValidationError', values => {
    const { actionLogicalId, selectedItems, selectedItemTypes, selectedElementLabels } = values;
    if (actionLogicalId && Boolean(actionLogicalId)) {
      return Boolean(selectedItems?.length || selectedItemTypes?.length || selectedElementLabels?.length);
    }
    return true;
  })
  .test('location-required', 'locationsValidationError', values => {
    const { actionLogicalId, selectedLocationTypes, selectedLocations } = values;
    if (actionLogicalId && ['enterZone', 'exitZone', 'presence'].includes(actionLogicalId)) {
      return Boolean(selectedLocationTypes?.length || selectedLocations?.length);
    }
    return true;
  })
  .test('reading-required', 'readingTypesValidationError', values => {
    const { actionLogicalId, selectedReadingTypes } = values;
    if (actionLogicalId && ['selectedReading'].includes(actionLogicalId)) {
      return Boolean(selectedReadingTypes?.length);
    }
    return true;
  })
  .test('status-required', 'statusValidationError', values => {
    const { actionLogicalId, indicatorTypeId } = values;
    if (actionLogicalId && ['changeStatus'].includes(actionLogicalId)) {
      return Boolean(indicatorTypeId);
    }
    return true;
  })
  .test('threshold-pair', function (values) {
    const v: any = values;
    const hasType = !!v.thresholdEventTypeId;
    const hasDelay = typeof v.timeoutConfig?.thresholdTriggerDelay === 'number' && v.timeoutConfig.thresholdTriggerDelay > 0;

    if (!hasType && !hasDelay) return true;
    if (hasType && hasDelay) return true;

    if (!hasType && hasDelay) {
      return this.createError({
        path: 'thresholdEventTypeId',
        //message: 'Selecciona el evento del umbral',
      });
    }

    return this.createError({
      path: 'timeoutConfig.thresholdTriggerDelay',
      // message: 'Indica el tiempo de umbral',
    });
  });

// remove when update gets refactor
const essentialValues = new Set([
  'actionLogicalId',
  'selectedDeviceTypes',
  'selectedItems',
  'selectedItemTypes',
  //'selectedGeometries',
  'selectedReadingTypes',
  'selectedElementLabels',
  'selectedDeviceLabels',
  'selectedLocations',
  'selectedLocationTypes',
  'actionConfig',
]);

const EGRPage = props => {
  const dispatch = useAppDispatch();

  const extraActionToDoAfterCorrectSubmit = async (values: CEGR, options: { itemId: string; responseData: CEGR }) => {
    if (options.responseData.hasWarning) {
      dispatch(globalActions.notification(['<b>ruleOverlap.title</b>', <br />, 'ruleOverlap.body']));
    }
  };

  return (
    <CrudPage
      key={'egrPage'}
      configComponent={defaultConfigComponent}
      form={EGRForm}
      defaultValues={defaultValues}
      validationSchema={validationSchema}
      essentialValues={essentialValues}
      extraActionToDoAfterCorrectSubmit={extraActionToDoAfterCorrectSubmit}
    />
  );
};

export default EGRPage;
